import React, { useState, useEffect, useCallback } from 'react';
import './CommonStyles.css';
import './InvoiceSettings.css';
import { Edit3, Trash2, Info, ChevronDown, Download, Printer, FileText, FileSpreadsheet } from 'lucide-react';
import apiService from '../../services/api';
import { toast } from 'react-toastify';

const InvoiceSettings = () => {
    const [activeSubTab, setActiveSubTab] = useState('schemes');
    const [searchQuery, setSearchQuery] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showAddLayoutModal, setShowAddLayoutModal] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState(null);
    const [expandedAccordion, setExpandedAccordion] = useState('general');
    const [selectedLayouts, setSelectedLayouts] = useState([]);

    // Data states
    const [schemes, setSchemes] = useState([]);
    const [layouts, setLayouts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingScheme, setEditingScheme] = useState(null);
    const [editingLayout, setEditingLayout] = useState(null);

    // Scheme form state
    const [schemeForm, setSchemeForm] = useState({
        name: '',
        scheme_type: 'blank',
        prefix: '',
        start_number: 1,
        total_digits: 4,
        number_type: 'sequential',
        is_default: 0
    });

    // Layout form state
    const [layoutForm, setLayoutForm] = useState({
        name: '',
        design: 'classic',
        header_text: '',
        show_logo: 0,
        show_letter_head: 0,
        is_default: 0
    });

    // Action Dropdown state
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

    const toggleDropdown = (id, event) => {
        event.stopPropagation();
        if (openDropdownId === id) {
            setOpenDropdownId(null);
        } else {
            const rect = event.currentTarget.getBoundingClientRect();
            const gap = 8;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const menuMinWidth = 160;
            const edgePadding = 8;

            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            const openUpwards = spaceBelow < 150 && spaceAbove > spaceBelow;
            const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
            const maxHeight = Math.max(120, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

            const left = Math.min(
                Math.max(rect.left - 100, edgePadding),
                Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
            );

            setDropdownPosition({
                top: openUpwards ? null : (rect.bottom + gap),
                bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
                left,
                maxHeight
            });
            setOpenDropdownId(id);
        }
    };

    // Click outside handler
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (!openDropdownId) return;
            const clickedActionButton = event.target.closest('.action-btn-trigger');
            const clickedDropdownMenu = event.target.closest('.action-dropdown-menu');
            if (clickedActionButton || clickedDropdownMenu) return;
            setOpenDropdownId(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdownId]);

    const fetchSchemes = useCallback(async () => {
        try {
            const res = await apiService.getInvoiceSchemesConnector();
            if (res.success && Array.isArray(res.data)) {
                setSchemes(res.data.map(s => ({
                    id: s.id,
                    name: s.name,
                    prefix: s.prefix_display || s.prefix || '--',
                    type: s.number_type_label || s.number_type,
                    start: s.start_number,
                    count: s.invoice_count,
                    digits: s.total_digits,
                    isDefault: s.is_default == 1
                })));
            } else {
                setSchemes([]);
            }
        } catch (err) {
      /* console.error('Error fetching schemes:', err); */
            setSchemes([]);
        }
    }, []);

    const fetchLayouts = useCallback(async () => {
        try {
            const res = await apiService.getInvoiceLayouts();
            if (res.success && Array.isArray(res.data)) {
                setLayouts(res.data.map(l => ({
                    id: l.id,
                    name: l.name,
                    design: l.design || 'classic',
                    isDefault: l.is_default == 1
                })));
            } else {
                setLayouts([]);
            }
        } catch (err) {
      /* console.error('Error fetching layouts:', err); */
            setLayouts([]);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchSchemes(), fetchLayouts()]);
            setLoading(false);
        };
        loadData();
    }, [fetchSchemes, fetchLayouts]);

    // ---- Scheme handlers ----
    const handleOpenAddScheme = () => {
        setEditingScheme(null);
        setSchemeForm({ name: '', scheme_type: 'blank', prefix: '', start_number: 1, total_digits: 4, number_type: 'sequential', is_default: 0 });
        setSelectedFormat(null);
        setShowAddModal(true);
    };

    const handleOpenEditScheme = (scheme) => {
        setEditingScheme(scheme);
        setSchemeForm({
            name: scheme.name,
            scheme_type: scheme.prefix && scheme.prefix.includes(String(new Date().getFullYear())) ? 'year' : 'blank',
            prefix: scheme.prefix || '',
            start_number: scheme.start,
            total_digits: scheme.digits,
            number_type: scheme.type?.toLowerCase().includes('random') ? 'random' : 'sequential',
            is_default: scheme.isDefault ? 1 : 0
        });
        setSelectedFormat(scheme.scheme_type === 'year' ? 'year-xxxx' : 'xxxx');
        setShowAddModal(true);
        setOpenDropdownId(null);
    };

    const handleSaveScheme = async () => {
        if (!schemeForm.name) { toast.error('Name is required'); return; }
        const payload = { ...schemeForm };
        if (selectedFormat === 'year-xxxx') payload.scheme_type = 'year';
        else payload.scheme_type = 'blank';
        try {
            if (editingScheme) {
                await apiService.updateInvoiceScheme(editingScheme.id, payload);
                toast.success('Invoice scheme updated successfully');
            } else {
                await apiService.createInvoiceScheme(payload);
                toast.success('Invoice scheme added successfully');
            }
            setShowAddModal(false);
            fetchSchemes();
        } catch (err) {
            toast.error(err.message || 'Failed to save invoice scheme');
        }
    };

    const handleDeleteScheme = async (id) => {
        if (!window.confirm('Are you sure you want to delete this invoice scheme?')) return;
        try {
            await apiService.deleteInvoiceScheme(id);
            toast.success('Invoice scheme deleted successfully');
            fetchSchemes();
        } catch (err) {
            toast.error(err.message || 'Failed to delete invoice scheme');
        }
        setOpenDropdownId(null);
    };

    const handleSetDefaultScheme = async (id) => {
        try {
            await apiService.setDefaultInvoiceScheme(id);
            toast.success('Default invoice scheme set successfully');
            fetchSchemes();
        } catch (err) {
            toast.error(err.message || 'Failed to set default');
        }
        setOpenDropdownId(null);
    };

    // ---- Layout handlers ----
    const handleOpenAddLayout = () => {
        setEditingLayout(null);
        setLayoutForm({ name: '', design: 'classic', header_text: '', show_logo: 0, show_letter_head: 0, is_default: 0 });
        setExpandedAccordion('general');
        setShowAddLayoutModal(true);
    };

    const handleOpenEditLayout = async (layout) => {
        try {
            setEditingLayout(layout);
            const res = await apiService.getInvoiceLayout(layout.id);
            const fullData = (res && res.success && res.data) ? res.data : layout;
            
            setLayoutForm({
                name: fullData.name || '',
                design: fullData.design || 'classic',
                header_text: fullData.header_text || '',
                invoice_no_prefix: fullData.invoice_no_prefix || '',
                invoice_heading: fullData.invoice_heading || '',
                sub_total_label: fullData.sub_total_label || '',
                discount_label: fullData.discount_label || '',
                tax_label: fullData.tax_label || '',
                total_label: fullData.total_label || '',
                highlight_color: fullData.highlight_color || '#000000',
                footer_text: fullData.footer_text || '',
                invoice_heading_not_paid: fullData.invoice_heading_not_paid || '',
                invoice_heading_paid: fullData.invoice_heading_paid || '',
                total_due_label: fullData.total_due_label || '',
                customer_label: fullData.customer_label || '',
                paid_label: fullData.paid_label || '',
                sub_heading_line1: fullData.sub_heading_line1 || '',
                sub_heading_line2: fullData.sub_heading_line2 || '',
                sub_heading_line3: fullData.sub_heading_line3 || '',
                sub_heading_line4: fullData.sub_heading_line4 || '',
                sub_heading_line5: fullData.sub_heading_line5 || '',
                table_product_label: fullData.table_product_label || '',
                table_qty_label: fullData.table_qty_label || '',
                table_unit_price_label: fullData.table_unit_price_label || '',
                table_subtotal_label: fullData.table_subtotal_label || '',
                client_id_label: fullData.client_id_label || '',
                date_label: fullData.date_label || '',
                quotation_heading: fullData.quotation_heading || '',
                quotation_no_prefix: fullData.quotation_no_prefix || '',
                client_tax_label: fullData.client_tax_label || '',
                cat_code_label: fullData.cat_code_label || '',
                cn_heading: fullData.cn_heading || '',
                cn_no_label: fullData.cn_no_label || '',
                cn_amount_label: fullData.cn_amount_label || '',
                sales_person_label: fullData.sales_person_label || '',
                prev_bal_label: fullData.prev_bal_label || '',
                date_time_format: fullData.date_time_format || '',
                change_return_label: fullData.change_return_label || '',
                round_off_label: fullData.round_off_label || '',
                commission_agent_label: fullData.commission_agent_label || '',
                show_business_name: fullData.show_business_name ? 1 : 0,
                show_location_name: fullData.show_location_name ? 1 : 0,
                show_landmark: fullData.show_landmark ? 1 : 0,
                show_city: fullData.show_city ? 1 : 0,
                show_state: fullData.show_state ? 1 : 0,
                show_country: fullData.show_country ? 1 : 0,
                show_zip_code: fullData.show_zip_code ? 1 : 0,
                show_mobile_number: fullData.show_mobile_number ? 1 : 0,
                show_alternate_number: fullData.show_alternate_number ? 1 : 0,
                show_email: fullData.show_email ? 1 : 0,
                show_tax_1: fullData.show_tax_1 ? 1 : 0,
                show_tax_2: fullData.show_tax_2 ? 1 : 0,
                show_logo: fullData.show_logo ? 1 : 0,
                show_barcode: fullData.show_barcode ? 1 : 0,
                show_payments: fullData.show_payments ? 1 : 0,
                show_customer: fullData.show_customer ? 1 : 0,
                show_client_id: fullData.show_client_id ? 1 : 0,
                show_brand: fullData.show_brand ? 1 : 0,
                show_sku: fullData.show_sku ? 1 : 0,
                show_cat_code: fullData.show_cat_code ? 1 : 0,
                show_sale_description: fullData.show_sale_description ? 1 : 0,
                show_sales_person: fullData.show_sales_person ? 1 : 0,
                show_expiry: fullData.show_expiry ? 1 : 0,
                show_lot: fullData.show_lot ? 1 : 0,
                show_previous_bal: fullData.show_previous_bal ? 1 : 0,
                show_image: fullData.show_image ? 1 : 0,
                show_reward_point: fullData.show_reward_point ? 1 : 0,
                show_qr_code: fullData.show_qr_code ? 1 : 0,
                show_commission_agent: fullData.show_commission_agent ? 1 : 0,
                show_letter_head: fullData.show_letter_head ? 1 : 0,
                is_default: fullData.is_default || (layout.isDefault ? 1 : 0)
            });
            setExpandedAccordion('general');
            setShowAddLayoutModal(true);
            setOpenDropdownId(null);
        } catch (err) {
            toast.error('Failed to load layout details');
        }
    };

    const handleSaveLayout = async () => {
        if (!layoutForm.name) { toast.error('Layout name is required'); return; }
        if (!layoutForm.design) { toast.error('Design is required'); return; }
        const formData = new FormData();
        Object.entries(layoutForm).forEach(([key, value]) => {
            formData.append(key, value);
        });
        try {
            if (editingLayout) {
                formData.append('_method', 'PUT');
                await apiService.updateInvoiceLayout(editingLayout.id, formData);
                toast.success('Invoice layout updated successfully');
            } else {
                await apiService.createInvoiceLayout(formData);
                toast.success('Invoice layout added successfully');
            }
            setShowAddLayoutModal(false);
            fetchLayouts();
        } catch (err) {
            toast.error(err.message || 'Failed to save invoice layout');
        }
    };

    const handleDeleteLayout = async (id) => {
        if (!window.confirm('Are you sure you want to delete this invoice layout?')) return;
        try {
            await apiService.deleteInvoiceLayout(id);
            toast.success('Invoice layout deleted successfully');
            fetchLayouts();
        } catch (err) {
            toast.error(err.message || 'Failed to delete invoice layout');
        }
        setOpenDropdownId(null);
    };

    const handleSetDefaultLayout = async (id) => {
        try {
            await apiService.setDefaultInvoiceLayout(id);
            toast.success('Default invoice layout set successfully');
            fetchLayouts();
        } catch (err) {
            toast.error(err.message || 'Failed to set default');
        }
        setOpenDropdownId(null);
    };

    const filteredSchemes = schemes.filter(s =>
        s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.prefix?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredLayouts = layouts.filter(l =>
        l.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const renderSchemesTable = () => (
        <div className="invoice-table-wrapper">
            {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>Loading...</div>
            ) : (
            <table className="invoice-table">
                <thead>
                    <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid var(--etpos-border, #e2e8f0)' }}>
                        {['Name', 'Prefix', 'Numbering Type', 'Start from', 'Invoice Count', 'Number of digits', 'Action'].map((header, idx) => (
                            <th key={idx} style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    {header}
                                    {header !== 'Action' && <Info size={14} style={{ color: '#06b6d4' }} />}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredSchemes.map((scheme) => (
                        <tr key={scheme.id} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                            <td style={{ padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <strong>{scheme.name}</strong>
                                    {scheme.isDefault && (
                                        <span style={{ background: '#10b981', color: 'white', fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>Default</span>
                                    )}
                                </div>
                            </td>
                            <td style={{ padding: '1rem' }}>{scheme.prefix || '--'}</td>
                            <td style={{ padding: '1rem' }}>{scheme.type}</td>
                            <td style={{ padding: '1rem' }}>{scheme.start}</td>
                            <td style={{ padding: '1rem' }}>{scheme.count}</td>
                            <td style={{ padding: '1rem' }}>{scheme.digits}</td>
                            <td style={{ padding: '0.75rem 1rem' }} className="actions-cell">
                                <div className="action-dropdown-container">
                                    <button
                                        className="action-btn-trigger"
                                        onClick={(e) => toggleDropdown(`scheme-${scheme.id}`, e)}
                                    >
                                        Action <span className="arrow-down">▼</span>
                                    </button>
                                    {openDropdownId === `scheme-${scheme.id}` && (
                                        <div
                                            className="action-dropdown-menu"
                                            style={{
                                                position: 'fixed',
                                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                left: `${dropdownPosition.left}px`,
                                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                                zIndex: 9999999
                                            }}
                                        >
                                            <button className="dropdown-item" onClick={() => handleOpenEditScheme(scheme)}>
                                                <Edit3 size={14} style={{ marginRight: '8px' }} /> Edit
                                            </button>
                                            <button className="dropdown-item delete-item" onClick={() => handleDeleteScheme(scheme.id)}>
                                                <Trash2 size={14} style={{ marginRight: '8px' }} /> Delete
                                            </button>
                                            {!scheme.isDefault && (
                                                <button className="dropdown-item" onClick={() => handleSetDefaultScheme(scheme.id)}>
                                                    <Info size={14} style={{ marginRight: '8px', color: '#06b6d4' }} /> Set as default
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            )}
        </div>
    );

    const renderAddModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', width: '600px', maxWidth: '95vw', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                {/* Modal Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{editingScheme ? 'Edit invoice scheme' : 'Add new invoice scheme'}</h3>
                    <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem', lineHeight: '1' }}>&times;</button>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* Format Selection */}
                    <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', alignItems: 'flex-start' }}>
                        <div
                            onClick={() => { setSelectedFormat('xxxx'); setSchemeForm(f => ({ ...f, scheme_type: 'blank' })); }}
                            style={{
                                width: '160px',
                                height: '100px',
                                background: 'var(--etpos-border, #e2e8f0)',
                                borderRadius: '4px',
                                padding: '1rem',
                                cursor: 'pointer',
                                border: selectedFormat === 'xxxx' ? '2px solid #3b82f6' : '1px solid transparent',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <div style={{ fontSize: '1.1rem', color: 'var(--etpos-text, #334155)' }}>FORMAT:</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--etpos-text, #334155)' }}>XXXX</div>
                        </div>
                        <div
                            onClick={() => { setSelectedFormat('year-xxxx'); setSchemeForm(f => ({ ...f, scheme_type: 'year' })); }}
                            style={{
                                width: '160px',
                                height: '100px',
                                background: 'var(--etpos-border, #e2e8f0)',
                                borderRadius: '4px',
                                padding: '1rem',
                                cursor: 'pointer',
                                border: selectedFormat === 'year-xxxx' ? '2px solid #3b82f6' : '1px solid transparent',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <div style={{ fontSize: '1.1rem', color: 'var(--etpos-text, #334155)' }}>FORMAT:</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--etpos-text, #334155)' }}>2026-XXXX</div>
                        </div>
                        <div style={{ marginLeft: '1rem' }}>
                            <div style={{ fontWeight: '700', color: 'var(--etpos-text, #101010)', marginBottom: '0.25rem' }}>Preview:</div>
                            <div style={{ color: 'var(--etpos-text-muted, #64748b)' }}>{selectedFormat ? (selectedFormat === 'xxxx' ? '0001' : '2026-0001') : 'Not selected'}</div>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.5rem' }}>Name:*</label>
                        <input
                            type="text"
                            placeholder="Name"
                            value={schemeForm.name}
                            onChange={(e) => setSchemeForm(f => ({ ...f, name: e.target.value }))}
                            style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', outline: 'none' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.5rem' }}>
                            Numbering Type:* <Info size={16} style={{ color: '#06b6d4' }} />
                        </label>
                        <div style={{ position: 'relative' }}>
                            <select
                                value={schemeForm.number_type}
                                onChange={(e) => setSchemeForm(f => ({ ...f, number_type: e.target.value }))}
                                style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', outline: 'none', background: 'var(--etpos-card, #fff)', appearance: 'none' }}
                            >
                                <option value="sequential">Sequential</option>
                                <option value="random">Aleatory/Random</option>
                            </select>
                            <ChevronDown size={16} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--etpos-text-muted, #64748b)', pointerEvents: 'none' }} />
                        </div>
                    </div>
                </div>

                {/* Modal Footer - Buttons in Middle */}
                <div style={{ padding: '1rem 1.5rem', borderTop: 'none', display: 'flex', justifyContent: 'center', gap: '0.5rem', paddingBottom: '2rem' }}>
                    <button onClick={handleSaveScheme} style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1.5rem', fontWeight: '600', cursor: 'pointer', minWidth: '80px' }}>
                        Save
                    </button>
                    <button onClick={() => { setShowAddModal(false); setEditingScheme(null); }} style={{ background: '#334155', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1.5rem', fontWeight: '600', cursor: 'pointer', minWidth: '80px' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const renderAddLayoutModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', width: '900px', maxWidth: '95vw', height: '90vh', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
                {/* Modal Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{editingLayout ? 'Edit invoice layout' : 'Add new invoice layout'}</h3>
                    <button onClick={() => setShowAddLayoutModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem', lineHeight: '1' }}>&times;</button>
                </div>

                {/* Modal Body - Scrollable */}
                <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                    {/* Compact Accordion Section */}
                    {[
                        { id: 'general', title: 'General Settings', icon: '📝' },
                        { id: 'labels', title: 'Headings & Labels', icon: '🏷️' },
                        { id: 'address', title: 'Address & Communication', icon: '📍' },
                        { id: 'products', title: 'Product Details', icon: '📦' },
                        { id: 'totals', title: 'Totals, QR & Others', icon: '📊' }
                    ].map((section) => (
                        <div key={section.id} style={{ marginBottom: '0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', overflow: 'hidden' }}>
                            <button
                                onClick={() => setExpandedAccordion(expandedAccordion === section.id ? null : section.id)}
                                style={{ width: '100%', padding: '1rem', background: 'var(--etpos-bg, #f8fafc)', border: 'none', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <span style={{ fontSize: '1.2rem' }}>{section.icon}</span> {section.title}
                                </span>
                                <ChevronDown
                                    size={18}
                                    style={{ transform: expandedAccordion === section.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                />
                            </button>

                            {expandedAccordion === section.id && (
                                <div style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
                                    {section.id === 'general' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.5rem' }}>Layout name:*</label>
                                                <input type="text" placeholder="Layout name" value={layoutForm.name || ''} onChange={(e) => setLayoutForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.5rem' }}>Design:*</label>
                                                <select value={layoutForm.design || 'classic'} onChange={(e) => setLayoutForm(f => ({ ...f, design: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }}>
                                                    <option value="classic">Classic (For normal printer)</option>
                                                    <option value="elegant">Elegant (For normal printer)</option>
                                                    <option value="detailed">Detailed (For normal printer)</option>
                                                    <option value="columnize-taxes">Columnize Taxes (For normal printer)</option>
                                                    <option value="slim">Slim (80mm)</option>
                                                    <option value="slim2">Slim 2 (58mm)</option>
                                                    <option value="encore">Encore (For normal printer)</option>
                                                </select>
                                                <small style={{ color: 'var(--etpos-text-muted, #64748b)' }}>Used for browser based printing</small>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                                                <input type="checkbox" id="showLetterHead" checked={!!layoutForm.show_letter_head} onChange={(e) => setLayoutForm(f => ({ ...f, show_letter_head: e.target.checked ? 1 : 0 }))} />
                                                <label htmlFor="showLetterHead" style={{ fontWeight: '600' }}>Show letter head</label>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.5rem' }}>Invoice Logo:</label>
                                                <input type="file" onChange={(e) => { if (e.target.files[0]) setLayoutForm(f => ({ ...f, logo: e.target.files[0] })); }} style={{ fontSize: '0.85rem' }} />
                                                <small style={{ display: 'block', color: '#94a3b8' }}>Max 1 MB, jpeg, gif, png formats only.</small>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input type="checkbox" id="showLogo" checked={!!layoutForm.show_logo} onChange={(e) => setLayoutForm(f => ({ ...f, show_logo: e.target.checked ? 1 : 0 }))} />
                                                <label htmlFor="showLogo" style={{ fontWeight: '600' }}>Show invoice Logo</label>
                                            </div>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.5rem' }}>Header text:</label>
                                                <textarea value={layoutForm.header_text || ''} onChange={(e) => setLayoutForm(f => ({ ...f, header_text: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', height: '100px' }}></textarea>
                                            </div>
                                        </div>
                                    )}

                                    {section.id === 'labels' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Invoice heading:</label>
                                                <input type="text" placeholder="Invoice heading" value={layoutForm.invoice_heading || ''} onChange={(e) => setLayoutForm(f => ({ ...f, invoice_heading: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Quotation Heading:</label>
                                                <input type="text" placeholder="Quotation Heading" value={layoutForm.quotation_heading || ''} onChange={(e) => setLayoutForm(f => ({ ...f, quotation_heading: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Invoice no. label:</label>
                                                <input type="text" placeholder="Invoice no. label" value={layoutForm.invoice_no_prefix || ''} onChange={(e) => setLayoutForm(f => ({ ...f, invoice_no_prefix: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Date Label:</label>
                                                <input type="text" placeholder="Date Label" value={layoutForm.date_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, date_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Due date label:</label>
                                                <input type="text" placeholder="Due date label" value={layoutForm.total_due_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, total_due_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Sales Person Label:</label>
                                                <input type="text" placeholder="Sales Person Label" value={layoutForm.sales_person_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, sales_person_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                        </div>
                                    )}

                                    {section.id === 'address' && (
                                        <div>
                                            <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>Address Fields to Show:</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                                {[
                                                    { key: 'show_landmark', label: 'Landmark' },
                                                    { key: 'show_city', label: 'City' },
                                                    { key: 'show_state', label: 'State' },
                                                    { key: 'show_country', label: 'Country' },
                                                    { key: 'show_zip_code', label: 'Zip Code' },
                                                    { key: 'show_mobile_number', label: 'Mobile number' },
                                                    { key: 'show_email', label: 'Email' },
                                                    { key: 'show_customer', label: 'Customer information' },
                                                    { key: 'show_client_id', label: 'Client ID' },
                                                    { key: 'show_business_name', label: 'Business name' },
                                                    { key: 'show_location_name', label: 'Location name' }
                                                ].map(item => (
                                                    <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input type="checkbox" id={item.key} checked={!!layoutForm[item.key]} onChange={(e) => setLayoutForm(f => ({ ...f, [item.key]: e.target.checked ? 1 : 0 }))} />
                                                        <label htmlFor={item.key} style={{ fontSize: '0.9rem' }}>Show {item.label}</label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {section.id === 'products' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Product Label:</label>
                                                <input type="text" placeholder="Product Label" value={layoutForm.table_product_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, table_product_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Quantity Label:</label>
                                                <input type="text" placeholder="Quantity Label" value={layoutForm.table_qty_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, table_qty_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Unit Price Label:</label>
                                                <input type="text" placeholder="Unit Price Label" value={layoutForm.table_unit_price_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, table_unit_price_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Subtotal Label:</label>
                                                <input type="text" placeholder="Subtotal Label" value={layoutForm.table_subtotal_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, table_subtotal_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>HSN Code Label:</label>
                                                <input type="text" placeholder="HSN Code Label" value={layoutForm.cat_code_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, cat_code_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                            </div>
                                            <div style={{ gridColumn: 'span 2', marginTop: '1rem' }}>
                                                <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>Product details to show:</h4>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                                    {[
                                                        { key: 'show_brand', label: 'Brand' },
                                                        { key: 'show_sku', label: 'SKU' },
                                                        { key: 'show_cat_code', label: 'HSN Code' },
                                                        { key: 'show_sale_description', label: 'Sale description' },
                                                        { key: 'show_expiry', label: 'Expiry' },
                                                        { key: 'show_lot', label: 'Lot' },
                                                        { key: 'show_image', label: 'Product image' }
                                                    ].map(item => (
                                                        <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <input type="checkbox" id={item.key} checked={!!layoutForm[item.key]} onChange={(e) => setLayoutForm(f => ({ ...f, [item.key]: e.target.checked ? 1 : 0 }))} />
                                                            <label htmlFor={item.key} style={{ fontSize: '0.9rem' }}>Show {item.label}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {section.id === 'totals' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                            <div style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                                <div>
                                                    <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Subtotal label:</label>
                                                    <input type="text" placeholder="Subtotal label" value={layoutForm.sub_total_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, sub_total_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Tax label:</label>
                                                    <input type="text" placeholder="Tax label" value={layoutForm.tax_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, tax_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Total label:</label>
                                                    <input type="text" placeholder="Total label" value={layoutForm.total_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, total_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Round off label:</label>
                                                    <input type="text" placeholder="Round off label" value={layoutForm.round_off_label || ''} onChange={(e) => setLayoutForm(f => ({ ...f, round_off_label: e.target.value }))} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem' }} />
                                                </div>
                                            </div>
                                            <div style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)' }}>
                                                <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--etpos-text, #1e293b)', fontSize: '0.95rem' }}>QR Code Settings</h4>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input type="checkbox" id="show_qr_code" checked={!!layoutForm.show_qr_code} onChange={(e) => setLayoutForm(f => ({ ...f, show_qr_code: e.target.checked ? 1 : 0 }))} />
                                                        <label htmlFor="show_qr_code" style={{ fontSize: '0.875rem' }}>Show QR Code</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Modal Footer */}
                <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                    <button onClick={handleSaveLayout} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '100px' }}>
                        {editingLayout ? 'Update' : 'Save'}
                    </button>
                    <button onClick={() => { setShowAddLayoutModal(false); setEditingLayout(null); }} style={{ background: '#334155', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '100px' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    const toggleLayoutSelection = (id) => {
        setSelectedLayouts(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const renderLayoutsTable = () => (
        <div className="invoice-table-wrapper">
            {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>Loading...</div>
            ) : (
            <table className="invoice-table">
                <thead>
                    <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid var(--etpos-border, #e2e8f0)' }}>
                        {['Layout Name', 'Design', 'Action'].map((header, idx) => (
                            <th key={idx} style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    {header}
                                    {header !== 'Action' && <Info size={14} style={{ color: '#06b6d4' }} />}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredLayouts.map((layout) => (
                        <tr key={layout.id} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                            <td style={{ padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <strong>{layout.name}</strong>
                                    {layout.isDefault && (
                                        <span style={{ background: '#10b981', color: 'white', fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>Default</span>
                                    )}
                                </div>
                            </td>
                            <td style={{ padding: '1rem' }}>{layout.design || 'Classic'}</td>
                            <td style={{ padding: '0.75rem 1rem' }} className="actions-cell">
                                <div className="action-dropdown-container">
                                    <button
                                        className="action-btn-trigger"
                                        onClick={(e) => toggleDropdown(`layout-${layout.id}`, e)}
                                    >
                                        Action <span className="arrow-down">▼</span>
                                    </button>
                                    {openDropdownId === `layout-${layout.id}` && (
                                        <div
                                            className="action-dropdown-menu"
                                            style={{
                                                position: 'fixed',
                                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                left: `${dropdownPosition.left}px`,
                                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                                zIndex: 9999999
                                            }}
                                        >
                                            <button className="dropdown-item" onClick={() => handleOpenEditLayout(layout)}>
                                                <Edit3 size={14} style={{ marginRight: '8px' }} /> Edit
                                            </button>
                                            <button className="dropdown-item delete-item" onClick={() => handleDeleteLayout(layout.id)}>
                                                <Trash2 size={14} style={{ marginRight: '8px' }} /> Delete
                                            </button>
                                            {!layout.isDefault && (
                                                <button className="dropdown-item" onClick={() => handleSetDefaultLayout(layout.id)}>
                                                    <Info size={14} style={{ marginRight: '8px', color: '#06b6d4' }} /> Set as default
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            )}
        </div>
    );

    return (
        <div className="invoice-settings-container">
            <div className="invoice-settings-card">
                {/* Tabs Row with Integrated Search */}
                <div className="invoice-tabs-row">
                    <div className="invoice-tabs-nav">
                        <button
                            onClick={() => setActiveSubTab('schemes')}
                            className={`invoice-tab-btn ${activeSubTab === 'schemes' ? 'active' : 'inactive'}`}
                        >
                            Invoice Schemes
                        </button>
                        <button
                            onClick={() => setActiveSubTab('layouts')}
                            className={`invoice-tab-btn ${activeSubTab === 'layouts' ? 'active' : 'inactive'}`}
                        >
                            Invoice Layouts
                        </button>
                    </div>

                    <div className="invoice-search-container">
                        <div style={{ position: 'relative', width: '100%' }}>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="invoice-search-input"
                            />
                        </div>
                    </div>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* Controls Row Below Tabs */}
                    <div className="invoice-controls-row">
                        <div className="invoice-entries-control">
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>Show</span>
                            <select style={{ padding: '0.3rem 0.5rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '4px', fontSize: '0.85rem' }}>
                                <option>25</option>
                                <option>50</option>
                                <option>100</option>
                            </select>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>entries</span>
                        </div>

                        <div className="invoice-actions-group">
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                                    style={{
                                        background: 'var(--etpos-card, #fff)',
                                        color: 'var(--etpos-text-muted, #64748b)',
                                        border: '1px solid var(--etpos-border, #e2e8f0)',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Download size={16} /> Export <ChevronDown size={14} />
                                </button>
                                {showExportDropdown && (
                                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '160px', padding: '0.5rem 0' }}>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileSpreadsheet size={16} color="#10b981" /> Export CSV
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <Printer size={16} color="#3b82f6" /> Print
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileText size={16} color="#ef4444" /> Export PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => activeSubTab === 'schemes' ? handleOpenAddScheme() : handleOpenAddLayout()}
                                style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1.25rem', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}
                            >
                                <span style={{ fontSize: '1.2rem', fontWeight: '400' }}>+</span> Add
                            </button>
                        </div>
                    </div>

                    {activeSubTab === 'schemes' ? renderSchemesTable() : renderLayoutsTable()}
                </div>

                {showAddModal && renderAddModal()}
                {showAddLayoutModal && renderAddLayoutModal()}
            </div>
        </div>
    );
};

export default InvoiceSettings;
